import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';

import {
  clearActivePersonImage,
  markOriginalPersonImageDeleted,
  updatePosition,
  movePosition,
  centerImage,
  updateScale,
  scaleBy,
  resetScale,
  updateCanvasSize,
  setVirtualTryOnResult,
  revertToOriginal,
  setLoading,
  setUploadProgress,
  setError,
  clearError,
  updateSettings,
  batchUpdate,
  resetPersonImageState,
  setCanvasIntegrationMode
} from '../redux/slices/personImageSlice';

import {
  selectPersonImage,
  selectActivePersonImageId,
  selectPosition,
  selectScale,
  selectCanvasSize,
  selectIsCanvasIntegrated,
  selectIsBackgroundRemoved,
  selectPersonImageActions,
  selectImageInfo,
  selectCanvasInfo,
  selectUploadStatus,
  selectScaleInfo,
  selectHasPersonImage,
  selectCanRevertToOriginal,
  selectIsVirtualTryOnResult,
  selectVirtualTryOnMetadata,
  selectOriginalPersonImageDeleted,
  selectError
} from '../redux/selectors/personImageSelectors';

import { constrainPosition, getDefaultPosition, getOptimalScale } from '../utils/personImageUtils';

/**
 * Custom hook for person image management
 * Provides actions and selectors for person image state
 */
export const usePersonImage = () => {
  const dispatch = useDispatch();

  // Selectors - use individual selectors to avoid identity function warnings
  const personImage = useSelector(selectPersonImage);
  const activePersonImageId = useSelector(selectActivePersonImageId);
  const position = useSelector(selectPosition);
  const scale = useSelector(selectScale);
  const canvasSize = useSelector(selectCanvasSize);
  const isCanvasIntegrated = useSelector(selectIsCanvasIntegrated);
  const isBackgroundRemoved = useSelector(selectIsBackgroundRemoved);

  const actions = useSelector(selectPersonImageActions);
  const imageInfo = useSelector(selectImageInfo);
  const canvasInfo = useSelector(selectCanvasInfo);
  const uploadStatus = useSelector(selectUploadStatus);
  const scaleInfo = useSelector(selectScaleInfo);
  const hasPersonImage = useSelector(selectHasPersonImage);
  const canRevert = useSelector(selectCanRevertToOriginal);
  const isVirtualResult = useSelector(selectIsVirtualTryOnResult);
  const virtualTryOnMetadata = useSelector(selectVirtualTryOnMetadata);
  const originalPersonImageDeleted = useSelector(selectOriginalPersonImageDeleted);
  const error = useSelector(selectError);

  // Construct displayProps from individual selectors
  const displayProps = {
    personImage,
    activePersonImageId,
    position,
    scale,
    backgroundRemoved: isBackgroundRemoved,
    isVirtualTryOnResult: isVirtualResult,
    metadata: virtualTryOnMetadata,
    canvasWidth: canvasSize.width,
    canvasHeight: canvasSize.height,
    isCanvasIntegrated
  };

  // Action creators
  const handleSetPersonImage = useCallback((imageData) => {
    console.error('[PersonImage] setPersonImage is removed. Use setActivePersonImage from usePersonImages hook instead.');
    // This is a deprecated function that should not be used
    // All person image management should go through usePersonImages hook
  }, []);

  const handleClearPersonImage = useCallback(() => {
    dispatch(clearActivePersonImage());
  }, [dispatch]);

  const handleMarkOriginalPersonImageDeleted = useCallback(() => {
    dispatch(markOriginalPersonImageDeleted());
  }, [dispatch]);

  const handleUpdatePosition = useCallback((position) => {
    dispatch(updatePosition(position));
  }, [dispatch]);

  const handleMovePosition = useCallback((delta) => {
    dispatch(movePosition(delta));
  }, [dispatch]);

  const handleCenterImage = useCallback(() => {
    dispatch(centerImage());
  }, [dispatch]);

  const handleUpdateScale = useCallback((scale) => {
    dispatch(updateScale(scale));
  }, [dispatch]);

  const handleScaleBy = useCallback((factor) => {
    dispatch(scaleBy(factor));
  }, [dispatch]);

  const handleResetScale = useCallback(() => {
    dispatch(resetScale());
  }, [dispatch]);

  const handleUpdateCanvasSize = useCallback((size) => {
    dispatch(updateCanvasSize(size));
  }, [dispatch]);

  const handleSetVirtualTryOnResult = useCallback((resultData) => {
    dispatch(setVirtualTryOnResult(resultData));
  }, [dispatch]);

  const handleRevertToOriginal = useCallback(() => {
    dispatch(revertToOriginal());
  }, [dispatch]);

  const handleSetLoading = useCallback((loading) => {
    dispatch(setLoading(loading));
  }, [dispatch]);

  const handleSetUploadProgress = useCallback((progress) => {
    dispatch(setUploadProgress(progress));
  }, [dispatch]);

  const handleSetError = useCallback((error) => {
    dispatch(setError(error));
  }, [dispatch]);

  const handleClearError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleUpdateSettings = useCallback((settings) => {
    dispatch(updateSettings(settings));
  }, [dispatch]);

  const handleBatchUpdate = useCallback((updates) => {
    dispatch(batchUpdate(updates));
  }, [dispatch]);

  const handleResetState = useCallback(() => {
    dispatch(resetPersonImageState());
  }, [dispatch]);

  const handleSetCanvasIntegrationMode = useCallback((integrated) => {
    dispatch(setCanvasIntegrationMode(integrated));
  }, [dispatch]);

  // Utility actions with constraints
  const handleConstrainedMove = useCallback((delta) => {
    const newPosition = {
      x: displayProps.position.x + delta.deltaX,
      y: displayProps.position.y + delta.deltaY
    };

    const constrainedPosition = constrainPosition(
      newPosition,
      { width: displayProps.canvasWidth, height: displayProps.canvasHeight },
      imageInfo.dimensions,
      displayProps.scale
    );

    dispatch(updatePosition(constrainedPosition));
  }, [dispatch, displayProps, imageInfo.dimensions]);

  const handleSmartCenter = useCallback(() => {
    if (imageInfo.dimensions.width && imageInfo.dimensions.height) {
      const centerPosition = getDefaultPosition(
        displayProps.canvasWidth,
        displayProps.canvasHeight,
        imageInfo.dimensions.width,
        imageInfo.dimensions.height,
        displayProps.scale
      );
      dispatch(updatePosition(centerPosition));
    }
  }, [dispatch, displayProps, imageInfo.dimensions]);

  const handleAutoFitScale = useCallback(() => {
    if (imageInfo.dimensions.width && imageInfo.dimensions.height) {
      const optimalScale = getOptimalScale(
        { width: displayProps.canvasWidth, height: displayProps.canvasHeight },
        imageInfo.dimensions
      );
      dispatch(updateScale(optimalScale));
    }
  }, [dispatch, displayProps, imageInfo.dimensions]);

  // Click handler for person image
  const handlePersonImageClick = useCallback((event) => {
    // Default behavior - could be extended for specific interactions
    // Handle person image click events here
  }, []);

  return {
    // State
    displayProps,
    actions,
    imageInfo,
    canvasInfo,
    uploadStatus,
    scaleInfo,
    hasPersonImage,
    canRevert,
    isVirtualResult,
    originalPersonImageDeleted,
    error,
    activePersonImageId,

    // Basic actions
    setPersonImage: handleSetPersonImage,
    clearPersonImage: handleClearPersonImage,
    markOriginalPersonImageDeleted: handleMarkOriginalPersonImageDeleted,
    updatePosition: handleUpdatePosition,
    movePosition: handleMovePosition,
    centerImage: handleCenterImage,
    updateScale: handleUpdateScale,
    scaleBy: handleScaleBy,
    resetScale: handleResetScale,
    updateCanvasSize: handleUpdateCanvasSize,
    setVirtualTryOnResult: handleSetVirtualTryOnResult,
    revertToOriginal: handleRevertToOriginal,
    setLoading: handleSetLoading,
    setUploadProgress: handleSetUploadProgress,
    setError: handleSetError,
    clearError: handleClearError,
    updateSettings: handleUpdateSettings,
    batchUpdate: handleBatchUpdate,
    resetState: handleResetState,

    // Enhanced actions
    constrainedMove: handleConstrainedMove,
    smartCenter: handleSmartCenter,
    autoFitScale: handleAutoFitScale,
    onPersonImageClick: handlePersonImageClick,
    setCanvasIntegrationMode: handleSetCanvasIntegrationMode
  };
};

export default usePersonImage;